import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.is_platform_admin ? "/plataforma" : "/operacional");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedUser = await login(email.trim(), password);
      toast.success("Bem-vindo ao Peça360");
      navigate(loggedUser.is_platform_admin ? "/plataforma" : "/operacional");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen almox-bg flex items-center justify-center p-4">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-800 lg:grid-cols-2 card-metal shadow-2xl">
        <div className="relative hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-sky-950/80 via-slate-950 to-slate-950 border-r border-slate-800">
          <Brand size="lg" />
          <div className="space-y-4">
            <h1 className="font-display text-5xl font-extrabold leading-none text-slate-50">
              Identificar.<br />Localizar.<br /><span className="text-sky-500">Prever.</span>
            </h1>
            <p className="text-slate-400 max-w-sm">
              Gestão inteligente de almoxarifado para empresas. Controle estoque, movimentações,
              inventários e compras em uma única plataforma.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
            <ShieldCheck className="h-4 w-4 text-emerald-500" /> Acesso restrito · Perfis auditados
          </div>
        </div>

        <div className="p-8 sm:p-10">
          <div className="lg:hidden mb-8"><Brand /></div>
          <h2 className="font-display text-3xl font-bold text-slate-50">Acessar sistema</h2>
          <p className="text-sm text-slate-400 mb-6">Entre com suas credenciais corporativas.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">E-mail</Label>
              <Input
                id="email"
                data-testid="input-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-slate-900/60 border-slate-700"
                placeholder="voce@suaempresa.com.br"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Senha</Label>
              <Input
                id="password"
                data-testid="input-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 bg-slate-900/60 border-slate-700"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="btn-login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-sky-700 hover:bg-sky-600 font-semibold text-base"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
